import * as React from 'react';
import { useState } from 'react';
import moment from 'moment-timezone';
import { SectionCard } from '../../../components/forms';
import { fullName, label, statusLabel } from './parts';

/* The production's activity log (ops_activity_log), newest first. Read-only. */

const FIELD: Record<string, string> = {
    eventName: 'event name',
    eventType: 'event type',
    deliveryAt: 'delivery',
    showReadyAt: 'show ready',
    startAt: 'start',
    endAt: 'end',
    venueId: 'venue',
    venueName: 'venue',
    producerId: 'producer',
    technicalLeadId: 'technical lead',
    logisticsCoordinatorId: 'logistics',
    textMessageBody: 'text message',
    dropboxFolderUrl: 'Dropbox link',
    guestCount: 'guest count',
};

const ACTION: Record<string, string> = {
    created: 'Created',
    updated: 'Edited',
    status_changed: 'Status changed',
    deleted: 'Deleted',
    crew_added: 'Crew added',
    crew_removed: 'Crew removed',
    crew_status_changed: 'Crew response changed',
};

const SHOWN = 12;

const obj = (v: any): Record<string, any> => {
    if (!v) return {};
    if (typeof v === 'string') {
        try {
            return JSON.parse(v) ?? {};
        } catch {
            return {};
        }
    }
    return v;
};

export default function TimelineSection({ activity, staff }: { activity: any[]; staff: any[] }) {
    const [all, setAll] = useState(false);
    const who = (staffId: any) => {
        const s = staff.find((x) => +x.id === +staffId);
        return s ? fullName(s) : 'Someone';
    };

    const detail = (e: any): string => {
        const before = obj(e.before);
        const after = obj(e.after);
        switch (e.action) {
            case 'status_changed':
                return `${statusLabel(before.status)} → ${statusLabel(after.status)}`;
            case 'updated': {
                const keys = Array.from(new Set(Object.keys(after).map((k) => FIELD[k] ?? label(k).toLowerCase())));
                return keys.length ? `Changed ${keys.join(', ')}` : 'No changes';
            }
            case 'crew_added':
                return [who(after.staffId), label(after.role).toLowerCase()].filter(Boolean).join(' · ');
            case 'crew_removed':
                return [who(before.staffId), label(before.role).toLowerCase()].filter(Boolean).join(' · ');
            case 'crew_status_changed':
                return `${who(after.staffId)}: ${label(before.status).toLowerCase()} → ${label(after.status).toLowerCase()}`;
            case 'created':
                if (after.duplicatedFrom) return `Duplicated from event ${after.duplicatedFrom}`;
                if (after.enabledOps) return 'Production added to an existing event';
                return after.status ? `As ${statusLabel(after.status).toLowerCase()}` : '';
            default:
                return '';
        }
    };

    const rows = all ? activity : activity.slice(0, SHOWN);

    return (
        <SectionCard title="Timeline" note={activity.length ? String(activity.length) : undefined}>
            {activity.length === 0 ? (
                <p className="quiet-empty">No activity yet.</p>
            ) : (
                <ol className="prd-tl">
                    {rows.map((e) => {
                        const d = detail(e);
                        const actor = e.actorName ?? e.actorUsername;
                        return (
                            <li key={e.id}>
                                <span className="prd-tl-dot" aria-hidden="true" />
                                <span className="prd-tl-b">
                                    <span className="nm2">
                                        {ACTION[e.action] ?? label(e.action)}
                                        {actor ? <span className="prd-tl-who"> · {actor}</span> : null}
                                    </span>
                                    {d && <span className="sub2">{d}</span>}
                                    <span className="prd-tl-at">{e.occurredAt ? moment(e.occurredAt).format('MMM D, h:mm A') : ''}</span>
                                </span>
                            </li>
                        );
                    })}
                </ol>
            )}
            {activity.length > SHOWN && (
                <button type="button" className="tb-btn sm prd-more" onClick={() => setAll((v) => !v)}>
                    {all ? 'Show fewer' : `Show all ${activity.length}`}
                </button>
            )}
        </SectionCard>
    );
}
